// src/components/admin/UserList.jsx
// Tabla de usuarios con filtros (Todos / Activos / Beta / Free / Test) y
// búsqueda por email. Click en "Ver" abre el modal de detalle.

import { useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import UserModal from './UserModal';

const MS = 86400000;
function tsMs(v) {
  if (!v) return null;
  if (typeof v === 'object' && typeof v.seconds === 'number') return v.seconds * 1000;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : null;
  }
  return null;
}
function isTest(email) {
  if (!email) return false;
  const e = String(email).toLowerCase();
  return e.includes('test') || e.includes('prueba');
}
function fmtDate(ms) {
  if (!ms) return '—';
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
}

const PLAN_LABEL = {
  monthly: 'Mensual', quarterly: 'Trimestral', biannual: 'Semestral',
};

function statusOf(u) {
  if (u.role === 'admin') return 'admin';
  if (isTest(u.email)) return 'test';
  // Beta nueva (campo betaAccess) tiene prioridad sobre legacy.
  const beta = u.betaAccess;
  if (beta && beta.active === true) {
    const betaTs = tsMs(beta.expiresAt);
    if (betaTs && betaTs > Date.now()) return 'beta';
    if (betaTs && betaTs <= Date.now()) return 'beta_expired';
  }
  const a = tsMs(u.accessUntil);
  if (a && a > Date.now()) return 'active';
  if (a && a <= Date.now()) return 'expired';
  // Legacy (planType==='Beta' o beta===true) — preservado por compatibilidad.
  if (u.planType === 'Beta' || u.beta === true) return 'beta';
  return 'free';
}

const PILL_LABEL = {
  admin: 'Admin', active: 'Activo', beta: 'Beta', beta_expired: 'Beta expirada',
  free: 'Free', test: 'Test', expired: 'Vencido',
};

function daysRemainingBeta(u) {
  const expMs = tsMs(u.betaAccess?.expiresAt);
  if (!expMs) return null;
  return Math.max(0, Math.ceil((expMs - Date.now()) / MS));
}

export default function UserList({ users }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [busyUid, setBusyUid] = useState(null);

  async function handleGrantBeta(uid) {
    if (busyUid) return;
    setBusyUid(uid);
    try {
      const call = httpsCallable(functions, 'adminGrantBetaAccess');
      await call({ uid });
    } catch (e) {
      console.error('[admin] grantBeta error:', e);
      alert('No se pudo activar la beta. Revisa la consola.');
    } finally {
      setBusyUid(null);
    }
  }

  async function handleRevokeBeta(uid) {
    if (busyUid) return;
    if (!window.confirm('¿Desactivar la beta de este usuario?')) return;
    setBusyUid(uid);
    try {
      const call = httpsCallable(functions, 'adminRevokeBetaAccess');
      await call({ uid });
    } catch (e) {
      console.error('[admin] revokeBeta error:', e);
      alert('No se pudo desactivar la beta. Revisa la consola.');
    } finally {
      setBusyUid(null);
    }
  }

  const enriched = useMemo(() => users.map((u) => ({ ...u, _status: statusOf(u) })), [users]);

  const filtered = useMemo(() => {
    return enriched.filter((u) => {
      if (filter !== 'all' && u._status !== filter) return false;
      if (search) {
        const s = search.toLowerCase().trim();
        if (!String(u.email || '').toLowerCase().includes(s)
          && !String(u.nombre || '').toLowerCase().includes(s)
          && !String(u.uid || '').toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [enriched, filter, search]);

  const filters = [
    { id: 'all', label: 'Todos' },
    { id: 'active', label: 'Activos' },
    { id: 'beta', label: 'Beta' },
    { id: 'free', label: 'Free' },
    { id: 'test', label: 'Test' },
  ];

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">Usuarios ({filtered.length} de {users.length})</h3>

      <div className="admin-filters">
        {filters.map((f) => (
          <button
            key={f.id}
            className="admin-filter-btn"
            data-active={filter === f.id}
            onClick={() => setFilter(f.id)}
            type="button"
          >
            {f.label}
          </button>
        ))}
        <input
          className="admin-search"
          type="text"
          placeholder="Buscar por email, nombre o UID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Nombre</th>
              <th>Plan</th>
              <th>Estado</th>
              <th>Registro</th>
              <th>Vence</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="admin-table-empty">Sin usuarios para los filtros aplicados.</td></tr>
            ) : (
              filtered.map((u) => {
                const created = tsMs(u.fechaCreacion) || tsMs(u.createdAt);
                const expires = tsMs(u.accessUntil);
                const planLabel = u.role === 'admin' ? 'Admin' : (PLAN_LABEL[u.lastPaymentPlan] || '—');
                return (
                  <tr key={u.uid}>
                    <td className="mono">{u.email || '—'}</td>
                    <td>{u.nombre || u.displayName || '—'}</td>
                    <td>{planLabel}</td>
                    <td><span className="admin-pill" data-tone={u._status}>{PILL_LABEL[u._status]}</span></td>
                    <td>{fmtDate(created)}</td>
                    <td>{fmtDate(expires)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {u._status === 'beta' && (
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: 'var(--pos-soft)', color: 'var(--pos)', whiteSpace: 'nowrap' }}>
                            BETA · {daysRemainingBeta(u)} día{daysRemainingBeta(u) !== 1 ? 's' : ''}
                          </span>
                        )}
                        {u._status === 'beta_expired' && (
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: 'var(--bg-subtle)', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                            BETA EXPIRADO
                          </span>
                        )}
                        {u._status !== 'admin' && u._status !== 'active' && (
                          <>
                            {u._status === 'beta' ? (
                              <button className="admin-action-btn" onClick={() => handleRevokeBeta(u.uid)} disabled={busyUid === u.uid} type="button" style={{ background: 'var(--neg-soft)', color: 'var(--neg)' }}>
                                {busyUid === u.uid ? '…' : 'Desactivar beta'}
                              </button>
                            ) : (
                              <button className="admin-action-btn" onClick={() => handleGrantBeta(u.uid)} disabled={busyUid === u.uid} type="button" style={{ background: 'var(--pos-soft)', color: 'var(--pos)' }}>
                                {busyUid === u.uid ? '…' : (u._status === 'beta_expired' ? 'Renovar beta 10 días' : 'Activar beta 10 días')}
                              </button>
                            )}
                          </>
                        )}
                        <button className="admin-action-btn" onClick={() => setSelected(u)} type="button">Ver</button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <UserModal user={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
